import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  TextArea,
  Control,
  Label,
  Select,
  SelectOption,
  PlusIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { partnersApi } from '@/api/partners';

interface CreatePartnerDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePartnerDialog = ({ onClose, onSuccess }: CreatePartnerDialogProps) => {
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [status, setStatus] = useState('onboarding');
  const [egressAllowlistNote, setEgressAllowlistNote] = useState('');
  const [notes, setNotes] = useState('');

  const { mutate: createPartner, isSubmitting } = useApiMutation<
    Parameters<typeof partnersApi.create>[0],
    void
  >({
    mutationFn: async (data) => {
      await partnersApi.create(data);
    },
    successMessage: 'Partner created successfully',
    errorMessage: 'Failed to create partner',
    onSuccess: () => onSuccess(),
  });

  const handleSubmit = async () => {
    if (!code.trim() || !displayName.trim()) return;
    await createPartner({
      code: code.trim().toUpperCase(),
      displayName: displayName.trim(),
      status,
      egressAllowlistNote: egressAllowlistNote.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Modal
      title="Add Partner"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<PlusIcon />}
          onClick={handleSubmit}
          disabled={!code.trim() || !displayName.trim() || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Add Partner'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <Control hideSpacing="bottom">
            <Label htmlFor="partner-code">Code</Label>
            <Input
              id="partner-code"
              placeholder="e.g., SCA1"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="partner-name">Display Name</Label>
            <Input
              id="partner-name"
              placeholder="e.g., South Carolina Electric & Gas"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="partner-status">Status</Label>
            <Select id="partner-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <SelectOption value="onboarding" label="Onboarding">
                Onboarding
              </SelectOption>
              <SelectOption value="active" label="Active">
                Active
              </SelectOption>
              <SelectOption value="paused" label="Paused">
                Paused
              </SelectOption>
              <SelectOption value="retired" label="Retired">
                Retired
              </SelectOption>
            </Select>
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="partner-egress">Egress Allowlist Note (Optional)</Label>
            <Input
              id="partner-egress"
              placeholder="e.g., 23.99.141.107/32"
              value={egressAllowlistNote}
              onChange={(e) => setEgressAllowlistNote(e.target.value)}
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="partner-notes">Notes (Optional)</Label>
            <TextArea
              id="partner-notes"
              rows={3}
              placeholder="Cutover status, contacts, migration notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Control>

          <p className="text-xs text-color-tertiary">
            After creating a partner, add its SFTP connection(s) and PGP keyring from the
            partner detail page before enabling a scheduled job in onering-gitops.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default CreatePartnerDialog;
